import React from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { Shield, Eye, EyeOff, Copy, ExternalLink, AlertCircle, Download } from 'lucide-react';
import toast from 'react-hot-toast';
import './GuestDashboard.css';

const GuestDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [revealedPassword, setRevealedPassword] = React.useState(false);

  // Check for shared credential
  const { data: credentialData, isLoading: credentialLoading } = useQuery(
    'my-shared-credential',
    async () => {
      const response = await api.get('/shares/my-shared-credential');
      return response.data;
    }
  );

  // Check for shared kubeconfig
  const { data: kubeconfigData, isLoading: kubeconfigLoading } = useQuery(
    'my-shared-kubeconfig',
    async () => {
      const response = await api.get('/shares/my-shared-kubeconfig');
      return response.data;
    }
  );

  // Check for shared VPN
  const { data: vpnData, isLoading: vpnLoading } = useQuery(
    'my-shared-vpn',
    async () => {
      const response = await api.get('/shares/my-shared-vpn');
      return response.data;
    }
  );

  const isLoading = credentialLoading || kubeconfigLoading || vpnLoading;
  const data = credentialData?.credential ? credentialData : (kubeconfigData?.kubeconfig ? kubeconfigData : vpnData);
  const resourceType = credentialData?.credential ? 'credential' : (kubeconfigData?.kubeconfig ? 'kubeconfig' : 'vpn');

  const handleCopy = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied to clipboard!`);
      setTimeout(async () => {
        await navigator.clipboard.writeText('');
      }, 30000);
    } catch (error) {
      toast.error('Failed to copy to clipboard');
    }
  };

  if (isLoading) {
    return (
      <div className="guest-dashboard">
        <div className="loading">Loading shared {resourceType}...</div>
      </div>
    );
  }

  const hasCredential = credentialData?.credential;
  const hasKubeconfig = kubeconfigData?.kubeconfig;
  const hasVpn = vpnData?.vpn;

  if (!hasCredential && !hasKubeconfig && !hasVpn) {
    return (
      <div className="guest-dashboard">
        <div className="empty-state">
          <Shield size={64} className="empty-icon" />
          <h2>No Active Share</h2>
          <p>You don't have access to any shared {resourceType === 'credential' ? 'credentials' : (resourceType === 'kubeconfig' ? 'kubeconfigs' : 'VPNs')} at this time.</p>
          <p className="hint">
            {data?.message || `Please contact the person who shared the ${resourceType === 'credential' ? 'credential' : (resourceType === 'kubeconfig' ? 'kubeconfig' : 'VPN')} with you.`}
          </p>
        </div>
      </div>
    );
  }

  const { credential, kubeconfig, vpn, share } = data;
  const resource = credential || kubeconfig || vpn;

  return (
    <div className="guest-dashboard">
      <div className="guest-header">
        <div className="header-content">
          <Shield size={32} className="header-icon" />
          <div>
            <h1>Shared {resourceType === 'credential' ? 'Credential' : (resourceType === 'kubeconfig' ? 'Kubeconfig' : 'VPN')} Access</h1>
            <p className="subtitle">You have been granted access to a {resourceType}</p>
          </div>
        </div>
        <a
          href={share.shareUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="share-link-btn"
        >
          <ExternalLink size={16} />
          <span>Open Share Link</span>
        </a>
      </div>

      <div className="credential-card">
        <div className="credential-header">
          <div>
            <h2>{resource.name}</h2>
            {resource.description && <p className="description">{resource.description}</p>}
            <div className="badges">
              {credential ? (
                <>
                  <span className="badge">{credential.category.replace(/_/g, ' ')}</span>
                  <span className="badge">{credential.type.replace(/_/g, ' ')}</span>
                </>
              ) : kubeconfig ? (
                <>
                  {kubeconfig.clusterName && <span className="badge">Cluster: {kubeconfig.clusterName}</span>}
                  {kubeconfig.contextName && <span className="badge">Context: {kubeconfig.contextName}</span>}
                </>
              ) : (
                <>
                  {vpn.serverAddress && <span className="badge">Server: {vpn.serverAddress}</span>}
                  {vpn.protocol && <span className="badge">Protocol: {vpn.protocol}</span>}
                </>
              )}
            </div>
          </div>
          <div className="shared-by">
            <span>Shared by: <strong>{resource.createdBy.username || resource.createdBy.email}</strong></span>
          </div>
        </div>

        {credential ? (
          <div className="credential-data">
          {credential.data.username && (
            <div className="data-field">
              <label>Username</label>
              <div className="field-value">
                <span>{credential.data.username}</span>
                <button
                  className="copy-btn"
                  onClick={() => handleCopy(credential.data.username, 'Username')}
                  title="Copy username"
                >
                  <Copy size={16} />
                </button>
              </div>
            </div>
          )}

          {credential.data.password && (
            <div className="data-field">
              <label>Password</label>
              <div className="field-value">
                <span className="password-field">
                  {revealedPassword ? credential.data.password : '••••••••'}
                </span>
                <div className="field-actions">
                  <button
                    className="copy-btn"
                    onClick={() => handleCopy(credential.data.password, 'Password')}
                    title="Copy password"
                  >
                    <Copy size={16} />
                  </button>
                  <button
                    className="copy-btn"
                    onClick={() => setRevealedPassword(!revealedPassword)}
                    title={revealedPassword ? 'Hide password' : 'Show password'}
                  >
                    {revealedPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
            </div>
          )}

          {credential.data.host && (
            <div className="data-field">
              <label>Host</label>
              <div className="field-value">
                <span>{credential.data.host}</span>
                <button className="copy-btn" onClick={() => handleCopy(credential.data.host, 'Host')}>
                  <Copy size={14} />
                </button>
              </div>
            </div>
          )}

          {credential.data.port && (
            <div className="data-field">
              <label>Port</label>
              <div className="field-value">
                <span>{credential.data.port}</span>
                <button className="copy-btn" onClick={() => handleCopy(String(credential.data.port), 'Port')}>
                  <Copy size={14} />
                </button>
              </div>
            </div>
          )}

          {credential.data.token && (
            <div className="data-field">
              <label>API Token</label>
              <div className="field-value">
                <span className="password-field">
                  {revealedPassword ? credential.data.token : '••••••••'}
                </span>
                <div className="field-actions">
                  <button
                    className="copy-btn"
                    onClick={() => handleCopy(credential.data.token, 'Token')}
                    title="Copy token"
                  >
                    <Copy size={16} />
                  </button>
                  <button
                    className="copy-btn"
                    onClick={() => setRevealedPassword(!revealedPassword)}
                    title={revealedPassword ? 'Hide token' : 'Show token'}
                  >
                    {revealedPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
        ) : kubeconfig ? (
          <div className="credential-data">
            <div className="data-field">
              <label>Kubeconfig Content</label>
              <div className="field-value">
                <textarea
                  readOnly
                  value={kubeconfig.data.content}
                  rows={15}
                  style={{ 
                    fontFamily: 'monospace', 
                    fontSize: '12px', 
                    width: '100%', 
                    padding: '10px',
                    borderRadius: '4px',
                    border: '1px solid #e5e7eb',
                    backgroundColor: '#f9fafb'
                  }}
                />
                <div style={{ marginTop: '10px', display: 'flex', gap: '10px' }}>
                  <button
                    className="copy-btn"
                    onClick={() => handleCopy(kubeconfig.data.content, 'Kubeconfig')}
                    title="Copy kubeconfig content"
                    style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
                  >
                    <Copy size={16} />
                    <span>Copy Content</span>
                  </button>
                  <button
                    className="copy-btn"
                    onClick={() => {
                      const blob = new Blob([kubeconfig.data.content], { type: 'application/x-yaml' });
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `${kubeconfig.name.replace(/[^a-z0-9]/gi, '_')}_kubeconfig.yaml`;
                      document.body.appendChild(a);
                      a.click();
                      window.URL.revokeObjectURL(url);
                      document.body.removeChild(a);
                      toast.success('Kubeconfig downloaded successfully!');
                    }}
                    title="Download kubeconfig"
                    style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
                  >
                    <Download size={16} />
                    <span>Download</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : vpn ? (
          <div className="credential-data">
            <div className="data-field">
              <label>VPN Config Content</label>
              <div className="field-value">
                <textarea
                  readOnly
                  value={vpn.data.content}
                  rows={15}
                  style={{ 
                    fontFamily: 'monospace', 
                    fontSize: '12px', 
                    width: '100%', 
                    padding: '10px',
                    borderRadius: '4px',
                    border: '1px solid #e5e7eb',
                    backgroundColor: '#f9fafb'
                  }}
                />
                <div style={{ marginTop: '10px', display: 'flex', gap: '10px' }}>
                  <button
                    className="copy-btn"
                    onClick={() => handleCopy(vpn.data.content, 'VPN')}
                    title="Copy VPN config content"
                    style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
                  >
                    <Copy size={16} />
                    <span>Copy Content</span>
                  </button>
                  <button
                    className="copy-btn"
                    onClick={() => {
                      const blob = new Blob([vpn.data.content], { type: 'application/octet-stream' });
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `${vpn.name.replace(/[^a-z0-9]/gi, '_')}_vpn.ovpn`;
                      document.body.appendChild(a);
                      a.click();
                      window.URL.revokeObjectURL(url);
                      document.body.removeChild(a);
                      toast.success('VPN config downloaded successfully!');
                    }}
                    title="Download VPN config"
                    style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
                  >
                    <Download size={16} />
                    <span>Download</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        <div className="share-info">
          <div className="info-item">
            <strong>Share Type:</strong> {share.shareToken ? 'Direct Link' : 'Email Invitation'}
          </div>
          <div className="info-item">
            <strong>One-time use:</strong> {share.isOneTimeUse ? 'Yes' : 'No'}
          </div>
          <div className="info-item">
            <strong>Expires:</strong> {share.expiresAt ? new Date(share.expiresAt).toLocaleString() : 'Never'}
          </div>
          {share.shareUrl && (
            <div className="info-item">
              <strong>Share Link:</strong>
              <div className="share-url">
                <input type="text" value={share.shareUrl} readOnly />
                <button
                  className="copy-btn"
                  onClick={() => handleCopy(share.shareUrl, 'Share link')}
                  title="Copy share link"
                >
                  <Copy size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="guest-notice">
        <AlertCircle size={20} />
        <div>
          <strong>Guest Account Notice:</strong>
          <p>You are accessing this {resourceType === 'credential' ? 'credential' : (resourceType === 'kubeconfig' ? 'kubeconfig' : 'VPN')} as a guest user. {share.expiresAt ? `Your access will expire on ${new Date(share.expiresAt).toLocaleString()}.` : 'Your access never expires.'}</p>
          {share.isOneTimeUse && (
            <p className="warning">This is a one-time use share. Once you access it, the link will be disabled.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default GuestDashboard;

